import React, { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import styles from '@common/inputFields/inputFields.module.scss';

import { useToast } from '@common/toast';
import VerificationModal from '@components/common/inputFields/vertificationModal';
import { useSaveKcbIdentity } from '@repository/identity/useIdentity';
import useProfileBasic from '@repository/profiles/useProfileBasic';
import { useUserInfo } from '@repository/auth/useUserInfo';
import KcbIpinVerificationRs from '@domain/rs/verification/KcbIpinVerificationRs';
import KcbSmsVerificationRs from '@domain/rs/verification/KcbSmsVerificationRs';
import { VerificationTextMap, VerificationType } from '@domain/constant/verificationType';
import { baseURL } from '@utils/common';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

interface UserIdentificationTypeProps {
  name: string;
  visible?: boolean;
}

const UserIdentificationType = ({ name, visible }: UserIdentificationTypeProps) => {
  const { watch, getValues } = useFormContext();
  const { field: userIdentificationTypeField } = useController({ name });
  const Toast = useToast();
  const { data: userInfo } = useUserInfo();
  const { refetch } = useProfileBasic(!!(userInfo?.id && visible));
  const { mutateAsync: saveKcbIdentityMutateAsync } = useSaveKcbIdentity((err) => {
    const errMessage = err.response?.data.message ?? '본인인증 실패하였습니다.';
    Toast({ type: 'error', iconType: 'info', content: errMessage });
    return;
  });
  const [isVisibleModal, setIsVisibleModal] = useState(false);

  const userIdentificationType = watch('userIdentificationType') as VerificationType;
  const isUserIdentificationType = !!userIdentificationType;

  const getIdentificationText = () => {
    if (!userIdentificationType && !!getValues('identified')) return '완료';
    if (userIdentificationType && !!getValues('identified'))
      return `${VerificationTextMap[userIdentificationType]} 본인인증`;
    return '미완료';
  };

  //본인인증 관련 child window 에서 넘어오는 완료 상태값 처리
  const handleVerificationEvent = async (event: MessageEvent) => {
    try {
      // 신뢰할 수 없는 origin 처리
      if (baseURL !== event.origin) return;

      const verificationRs: KcbSmsVerificationRs | KcbIpinVerificationRs = JSON.parse(event.data);
      //ipin 인증 성공 : T000, sms 인증 성공 : B000
      if (!['T000', 'B000'].includes(verificationRs.code)) {
        Toast({ type: 'error', iconType: 'info', content: '본인인증 실패하였습니다.' });
        return;
      }

      const res = await saveKcbIdentityMutateAsync(verificationRs);
      if (res) {
        console.log(res);
        await refetch();
        Toast({ type: 'success', iconType: 'check', content: '성공적으로 인증되었습니다.' });
      }
    } catch (e) {
      console.error(e);
    }
  };

  //아이핀 인증 완료시 이벤트 리스너 등록
  useEffect(() => {
    window.addEventListener('message', handleVerificationEvent, false);
    return () => window.removeEventListener('message', handleVerificationEvent);
  }, []);

  return (
    <div className={cx('userIdentificationTypeArea')}>
      <input
        type="text"
        className={cx('input')}
        placeholder="미완료"
        autoComplete="no"
        {...userIdentificationTypeField}
      />
      <div className={cx('userIdentificationTypeAreaWrap')}>
        <span className={cx('label', { empty: !isUserIdentificationType })}>{getIdentificationText()}</span>
      </div>
      <div className={cx('mobileBtnArea')}>
        <button className={cx('btn')} type="button" onClick={() => setIsVisibleModal(true)}>
          변경
        </button>
      </div>
      <VerificationModal visible={isVisibleModal} onClose={() => setIsVisibleModal(false)} />
    </div>
  );
};

export default UserIdentificationType;
